import { type DateArray, type EventAttributes, createEvent } from 'ics';
import moment from 'moment';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import { AppleLogoIcon, GoogleLogoIcon, MicrosoftOutlookIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	ChevronRightIcon,
	FlexGap,
	Heading3,
	Modal,
	Paragraph,
	Popover,
	QuillText,
	Separator,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import CalendarIcon from '../components/CalendarIcon';
import LearningEventAttendee from '../components/LearningEventAttendee';
import LearningEventAttendeeAvatars from '../components/LearningEventAttendeeAvatars';
import LearningEventHeader from '../components/LearningEventHeader';
import useTimezoneOffset from '../hooks/useTimezoneOffset';

const noWrapStyle = `
	display: block;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	width: 100%;
`;

const PlainAnchor = styled.a<{ noWrap?: boolean }>`
	color: ${tokens.brand.text};

	${(props) => (props.noWrap ? noWrapStyle : '')}

	&:hover {
		text-decoration: underline;

		&:active {
			color: ${tokens.text.primary};
		}
	}
`;

const LearningEventDetailLabel = styled(Box)`
	font-size: ${tokens.typography.sizes.body}px;
	color: ${tokens.text.secondary};
`;

const Alert = styled(Box)`
	background-color: ${tokens.error.background};
	border-radius: ${tokens.radius.sm}px;
	color: ${tokens.error.text};
`;

const TimezoneHint = styled(Paragraph).attrs({ small: true, mb: 0 })`
	display: inline;
	margin-left: ${tokens.gap.element}px;
	color: ${tokens.text.tertiary};
`;

const toDateArray = (m: moment.Moment): DateArray => [
	m.year(),
	m.month() + 1,
	m.date(),
	m.hours(),
	m.minutes(),
];

const makeTime = (m: moment.Moment) => m.toISOString().replace(/[-:]|\.\d{3}/g, '');

function LearningEventDetail(props: { label: string; value: ReactNode }) {
	return (
		<Flex py={1}>
			<LearningEventDetailLabel flexGrow={1}>{props.label}</LearningEventDetailLabel>
			<Box width={300} flexShrink={0} flexGrow={0} textAlign="right">
				{props.value}
			</Box>
		</Flex>
	);
}

function LearningEventModal(props: { event: graphql.LearningEvent; returnURL?: string }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const { hasDifference, cityName, formatInEventTimezone } = useTimezoneOffset(
		props.event.timezone,
		props.event.startDate,
	);
	const history = useHistory();
	const roles = useViewerRoles();

	const organizers = useMemo(
		() =>
			props.event.hosts.edges.filter(
				({ attendeeType }) =>
					attendeeType === graphql.LearningEventAttendeeType.Organizer ||
					attendeeType === graphql.LearningEventAttendeeType.HostOrganizer,
			),
		[props.event.hosts.edges],
	);

	const hosts = useMemo(
		() =>
			props.event.hosts.edges.filter(
				({ attendeeType }) =>
					attendeeType === graphql.LearningEventAttendeeType.Host ||
					attendeeType === graphql.LearningEventAttendeeType.HostOrganizer,
			),
		[props.event.hosts.edges],
	);

	const title = useMemo(() => localize(props.event.title).text, [localize, props.event.title]);
	const localizedDescription = useMemo(
		() => localize(props.event.description ?? [])?.text,
		[localize, props.event.description],
	);
	const description = useMemo(
		() =>
			[
				localizedDescription?.ops.map((op) => op.insert ?? '').join('') ?? undefined,
				props.event.meetingLink ?? undefined,
			]
				.filter(Boolean)
				.join(' - '),
		[localizedDescription, props.event.meetingLink],
	);
	const location = useMemo(() => props.event.location ?? '', [props.event.location]);

	const start = useMemo(() => moment.utc(props.event.startDate), [props.event.startDate]);

	const end = useMemo(() => moment.utc(props.event.endDate), [props.event.endDate]);

	const downloadIcs = useCallback(() => {
		const eventAttributes: EventAttributes = {
			title,
			description,
			location,
			start: toDateArray(start),
			end: toDateArray(end),
			startInputType: 'utc',
			startOutputType: 'utc',
			endInputType: 'utc',
			endOutputType: 'utc',
			url: window.location.href,
		};

		const { value } = createEvent(eventAttributes);
		if (!value) return;

		const blob = new Blob([value], { type: 'text/calendar' });
		const url = URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `${title}.ics`;
		anchor.style.position = 'absolute';
		anchor.style.pointerEvents = 'none';
		anchor.style.opacity = '0';
		document.body.appendChild(anchor);
		anchor.click();
		setTimeout(() => {
			anchor.remove();
			URL.revokeObjectURL(url);
		}, 0);
	}, [title, description, location, start, end]);

	const cancelled = useMemo(
		() => props.event.status === graphql.LearningEventStatus.Cancelled,
		[props.event.status],
	);

	const canDuplicate = useMemo(
		() => roles.isWorkspaceSuperuser || roles.isLearningOfficer,
		[roles.isWorkspaceSuperuser, roles.isLearningOfficer],
	);

	const handleOnCopyClick = useCallback(() => {
		history.push({
			pathname: `/learning/events/copy?eventID=${props.event.id}`,
			state: {
				backgroundLocation: history.location,
			},
		});
	}, [history, props.event.id]);

	const openGoogleCalendar = useCallback(() => {
		window.open(
			`https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${makeTime(start)}/${makeTime(end)}&location=${encodeURIComponent(location)}&text=${encodeURIComponent(title)}&details=${encodeURIComponent(description)}`,
		);
	}, [title, description, location, start, end, makeTime]);

	const openOutlookWeb = useCallback(() => {
		const startdt = start.toISOString();
		const enddt = end.toISOString();
		window.open(
			`https://outlook.office.com/calendar/deeplink/compose?path=/calendar/action/compose&rru=addevent&startdt=${encodeURIComponent(startdt)}&enddt=${encodeURIComponent(enddt)}&subject=${encodeURIComponent(title)}&location=${encodeURIComponent(location)}&body=${encodeURIComponent(description)}&allday=false`,
		);
	}, [title, description, location, start, end]);

	const handleClose = useCallback(() => {
		if (props.returnURL) {
			return history.push(props.returnURL);
		}

		if (history.location.state?.backgroundLocation) {
			return history.push(history.location.state.backgroundLocation);
		}

		history.push('/learning');
	}, [history, props.returnURL]);

	return (
		<Popover center visible>
			<Modal>
				<Modal.Header
					title={t('learning.event.modal.title')}
					rightAccessory={
						<Flex alignItems="center">
							<LearningEventAttendeeAvatars event={props.event} />
							<CalendarIcon date={props.event.startDate} />
						</Flex>
					}
					onClose={handleClose}
				/>

				<Modal.Contents p={0}>
					<LearningEventHeader event={props.event} />

					<Box p={4}>
						{cancelled && (
							<Alert py={2} px={3} mb={3}>
								{t('learning.event.modal.cancelled')}
							</Alert>
						)}

						<Box mb={2}>
							<Heading3>{t('learning.event.modal.actions.title')}</Heading3>
						</Box>

						<Box mb={3}>
							<Separator />
						</Box>

						<FlexGap mb={4} gap={tokens.gap.element} flexWrap="wrap">
							<Button
								type="secondary"
								link={`/learning/event/${props.event.id}/attendees`}
								navigationState={{
									backgroundLocation: history.location.state?.backgroundLocation,
								}}
								title={t('learning.event.modal.attendees.viewAll')}
								small
							/>
							{canDuplicate && (
								<Button
									type="secondary"
									onClick={handleOnCopyClick}
									navigationState={{
										backgroundLocation: history.location.state?.backgroundLocation,
									}}
									title={t('learning.event.modal.attendees.duplicate')}
									small
								/>
							)}
						</FlexGap>

						{!cancelled && (
							<Box mb={4}>
								<Box my={2}>
									<Heading3>{t('learning.event.modal.addToCalendar.button.title')}</Heading3>
								</Box>

								<Box mb={3}>
									<Separator />
								</Box>

								<CalendarButtons>
									<Button
										small
										type="secondary"
										width="100%"
										title="Apple Calendar"
										icon={
											<SmallerIcon>
												<AppleLogoIcon />
											</SmallerIcon>
										}
										onClick={downloadIcs}
									/>
									<Button
										small
										type="secondary"
										width="100%"
										title="Google Calendar"
										icon={
											<SmallerIcon>
												<GoogleLogoIcon />
											</SmallerIcon>
										}
										onClick={openGoogleCalendar}
									/>
									<Button
										small
										type="secondary"
										width="100%"
										title="Outlook Web"
										icon={<MicrosoftOutlookIcon />}
										onClick={openOutlookWeb}
									/>
									<Button
										small
										type="secondary"
										width="100%"
										title="Outlook Desktop"
										icon={<MicrosoftOutlookIcon />}
										onClick={downloadIcs}
									/>
								</CalendarButtons>
							</Box>
						)}

						<Box mb={2}>
							<Heading3>{t('learning.event.modal.introduction.title')}</Heading3>
						</Box>

						<Box mb={2}>
							<Separator />
						</Box>

						<Box mb={4}>
							<QuillText value={localizedDescription} />
						</Box>

						<Box mb={2}>
							<Heading3>{t('learning.event.modal.details.title')}</Heading3>
						</Box>

						<Separator />

						<Box mb={4} mt={2}>
							<LearningEventDetail
								label={t('learning.event.modal.details.startTime.label')}
								value={
									<>
										{formatInEventTimezone(props.event.startDate) ??
											moment(props.event.startDate).format('HH:mm')}
										{hasDifference && (
											<TimezoneHint>
												{t('learning.event.timezone.hint', {
													time: moment(props.event.startDate).format('HH:mm'),
												})}
											</TimezoneHint>
										)}
									</>
								}
							/>
							<LearningEventDetail
								label={t('learning.event.modal.details.endTime.label')}
								value={
									<>
										{formatInEventTimezone(props.event.endDate) ??
											moment(props.event.endDate).format('HH:mm')}
										{hasDifference && (
											<TimezoneHint>
												{t('learning.event.timezone.hint', {
													time: moment(props.event.endDate).format('HH:mm'),
												})}
											</TimezoneHint>
										)}
									</>
								}
							/>
							<LearningEventDetail
								label={t('learning.event.modal.meta.language.label')}
								value={t(`common.language.${props.event.languageID}`)}
							/>
							{props.event.location && (
								<LearningEventDetail
									label={t('learning.event.modal.details.location.label')}
									value={props.event.location}
								/>
							)}
							{props.event.meetingLink && (
								<LearningEventDetail
									label={t('learning.event.modal.details.meetingLink.label')}
									value={
										<PlainAnchor href={props.event.meetingLink} target="_blank" noWrap>
											{props.event.meetingLink}
										</PlainAnchor>
									}
								/>
							)}
							<LearningEventDetail
								label={t('learning.event.modal.details.maxAttendees.label')}
								value={props.event.capacity.toFixed()}
							/>
						</Box>

						{organizers.length > 0 && (
							<>
								<Box my={2}>
									<Heading3>{t('learning.event.modal.organizers.title')}</Heading3>
								</Box>

								<Separator />

								<Box mt={2} mb={4}>
									{organizers.map((host) => (
										<LearningEventAttendee
											key={host.node.id}
											user={host.node}
											type={host.attendeeType}
											readonly={true}
										/>
									))}
								</Box>
							</>
						)}

						{hosts.length > 0 && (
							<>
								<Box my={2}>
									<Heading3>{t('learning.event.modal.hosts.title')}</Heading3>
								</Box>

								<Separator />

								<Box mb={4}>
									{hosts.map((host) => (
										<LearningEventAttendee
											key={host.node.id}
											user={host.node}
											type={host.attendeeType}
											readonly={true}
										/>
									))}
								</Box>
							</>
						)}

						<Box mb={4}>
							<Button
								type="secondary"
								small
								link={`/learning/event/${props.event.id}/attendees`}
								navigationState={{
									backgroundLocation: history.location.state?.backgroundLocation,
								}}
								title={t('learning.event.modal.attendees.viewAll')}
								rightIcon={<ChevronRightIcon />}
							/>
						</Box>

						{cancelled && (
							<>
								<Box my={2}>
									<Heading3>{t('learning.event.modal.actions.title')}</Heading3>
								</Box>

								<Box mb={3}>
									<Separator />
								</Box>

								<Box mt={4}>
									<ActionArchiveListItem
										archivableItem={props.event}
										archivableItemName="event"
										archivableItemKey="event"
										useArchive={graphql.useArchiveLearningEventMutation as any}
										useRestore={graphql.useRestoreLearningEventMutation as any}
									/>
								</Box>
							</>
						)}
					</Box>
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}

export default function LearningEventModalID(props: { eventID: string; returnURL?: string }) {
	const { data } = graphql.useLearningEventQuery({
		variables: {
			id: props.eventID,
		},
	});

	if (data) {
		return <LearningEventModal event={data.learningEvent as graphql.LearningEvent} />;
	}

	return (
		<Popover center visible>
			<Modal>
				<Spinner centered />
			</Modal>
		</Popover>
	);
}

const CalendarButtons = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: ${tokens.gap.element}px;
	width: 100%;
	min-width: 0;

	> * {
		min-width: 0;
		max-width: 100%;
		overflow: hidden;
	}
`;

const SmallerIcon = styled(Box)`
	svg {
		scale: 80%;
	}
`;
